<template>
  <div class="space-y-6 pb-10">
    <!-- Page Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-800">Curriculum</h2>
        <p class="text-sm text-gray-500 mt-0.5">Map courses to programmes by level and semester</p>
      </div>
      <Button size="small"
        label="New Curriculum"
        icon="pi pi-plus"
        @click="showCreateForm = true"
      />
    </div>

    <details class="rounded-lg border p-3 text-sm text-gray-600">
      <summary class="cursor-pointer font-medium text-gray-800">How curriculum and modules work together</summary>
      <p class="mt-2">
        Curriculum determines which courses fit a student’s level/year and semester. For Modular or Residence
        programmes, those courses must also belong to an active module whose dates are open in the current
        session and whose semester pin matches, if set.
      </p>
      <p class="mt-2">
        To assign several courses, use the table checkboxes, choose Add to module, then click Assign module.
        Select records from the same programme. This adds an offering without removing previous module
        assignments.
      </p>
      <p class="mt-2">Curriculum changes affect course eligibility; they do not automatically rewrite existing student registrations.</p>
    </details>

    <!-- Create Form Drawer (overlay) -->
    <SlideOverDrawer
      v-model:visible="showCreateForm"
      header="Create Curriculum Entry"
      width="640px"
    >
      <div class="p-1 space-y-5">
          <!-- Hint -->
          <div class="flex items-start gap-2 bg-primary/10 border border-primary/20 px-3 py-2.5 rounded-lg">
            <i class="pi pi-info-circle text-primary mt-0.5 shrink-0 text-sm"></i>
            <span class="text-xs text-primary-700">
              Select a programme, level, and semester. Then add one or more courses with their status and submit.
              <br class="mt-1" />
              <strong>Level:</strong> Pick a specific level (e.g. 100) or <em>Any Level (*)</em> to make the course available at all levels.
              <strong>Semester:</strong> Pick a specific semester or <em>Any Semester (0)</em> to make it available in both semesters.
            </span>
          </div>

          <!-- Programme, Level, Semester -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-gray-600">Programme *</label>
              <Select
                v-model="programme_id"
                :options="programmeOptions"
                optionLabel="label"
                optionValue="id"
                placeholder="Select Programme"
                class="w-full"
                filter
                :virtualScroller="true"
                :itemSize="40"
                :invalid="!!createForm.fieldError('programme_id')"
                data-testid="curriculum-programme"
                @scroll="handleProgrammeScroll"
              />
              <small v-if="createForm.fieldError('programme_id')" class="text-red-500 text-xs" data-testid="curriculum-programme-error">
                {{ createForm.fieldError('programme_id') }}
              </small>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-gray-600">Level *</label>
              <Select v-model="course_registration_level" :options="levelOptions" optionLabel="label"
                optionValue="value" placeholder="Select level" class="w-full"
                :invalid="!!createForm.fieldError('course_registration_level')" data-testid="curriculum-level" />
              <small v-if="createForm.fieldError('course_registration_level')" class="text-red-500 text-xs">
                {{ createForm.fieldError('course_registration_level') }}
              </small>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-gray-600">Semester *</label>
              <Select v-model="semester" :options="createTermOptions" optionLabel="label" optionValue="value"
                placeholder="Select semester" class="w-full"
                :invalid="!!createForm.fieldError('semester')" data-testid="curriculum-semester" />
              <small v-if="createForm.fieldError('semester')" class="text-red-500 text-xs">
                {{ createForm.fieldError('semester') }}
              </small>
              <small v-if="selectedProgrammeIsYearBased" class="text-xs text-gray-500">
                Year-based programmes still use cumulative semesters: Year 1 = 1–2, Year 2 = 3–4. Choose Any Semester (0) for eligibility across semesters at the selected level/year.
              </small>
            </div>

            <div v-if="createUsesModules" class="flex flex-col gap-1">
              <label class="text-xs font-medium text-gray-600">Module</label>
              <Select v-model="programme_module_id" :options="createModuleOptions" optionLabel="label"
                optionValue="value" placeholder="Select Module (optional)" class="w-full" showClear
                :loading="loadingCreateModules" data-testid="curriculum-module-select" />
              <small class="text-xs text-gray-500">
                You can assign a module later. Until assigned to an open module, these courses will not be
                available for student registration in this modular programme.
              </small>
            </div>
          </div>

          <!-- Courses Section -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-medium text-gray-600">
                Courses *
                <span v-if="coursesSelected.length > 1" class="text-gray-400 font-normal ml-1">({{ coursesSelected.length }})</span>
              </label>
            </div>

            <DataTable :value="coursesSelected" size="small">
              <Column header="#" style="width: 3rem">
                <template #body="{ index }">
                  <span class="text-gray-400 font-mono text-xs">{{ index + 1 }}</span>
                </template>
              </Column>
              <Column header="Course">
                <template #body="{ data }">
                  <Select v-model="data.course_id" :options="courseOptions" optionLabel="label" optionValue="value"
                    placeholder="Select Course" class="w-full" filter
                    :virtualScrollerOptions="{ itemSize: 38 }" />
                </template>
              </Column>
              <Column header="Status" style="width: 10rem">
                <template #body="{ data }">
                  <Select v-model="data.status" :options="statusOptions" optionLabel="label" optionValue="value"
                    placeholder="Status" class="w-full" />
                </template>
              </Column>
              <Column style="width: 3rem">
                <template #body="{ index }">
                  <Button
                    icon="pi pi-times"
                    class="p-button-danger p-button-text p-button-rounded p-button-sm"
                    @click="removeCourse(index)"
                    :disabled="coursesSelected.length === 1"
                  />
                </template>
              </Column>
            </DataTable>

            <small v-if="createForm.fieldError('course')" class="block text-red-500 text-xs mt-1" data-testid="curriculum-courses-error">
              {{ createForm.fieldError('course') }}
            </small>

            <div class="flex justify-start mt-2">
              <Button
                label="Add Course"
                icon="pi pi-plus"
                class="p-button-outlined p-button-sm !text-xs"
                @click="addCourse"
              />
            </div>
          </div>

      </div>

      <template #footer>
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm text-gray-500">
            <span class="font-semibold text-gray-700">{{ selectedCourseCount }}</span>
            course<span v-if="selectedCourseCount !== 1">s</span>
            · <span class="font-semibold text-primary">{{ selectedUnitTotal }}</span> units
          </span>
          <Button size="small"
            @click="submit"
            :loading="isSubmitting"
            label="Submit Curriculum"
            icon="pi pi-check"
          />
        </div>
      </template>
    </SlideOverDrawer>

    <!-- View Toggle -->
    <div class="flex items-center gap-3">
      <SelectButton v-model="curriculumView" :options="curriculumViewOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
    </div>

    <!-- Unfloated Courses -->
    <UnfloatedCourses v-if="curriculumView === 'unfloated'" />

    <!-- Prerequisites -->
    <CoursePrerequisites v-if="curriculumView === 'prerequisites' && canViewPrerequisites" />

    <!-- Filters -->
    <BillingFilterBar
      v-if="curriculumView === 'curriculum'"
      :search="search || ''"
      search-placeholder="Search curriculum..."
      :chips="filterChips"
      @update:search="search = $event"
      @remove-chip="removeFilterChip"
      @clear="clearAllFilters"
    >
      <template #filters>
        <Select v-model="filterProgrammeId" :options="programmeOptionsWithAll" optionLabel="label" optionValue="value" placeholder="Programme" size="small" class="w-48" filter />
        <Select v-model="filterLevel" :options="levelOptionsWithAll" optionLabel="label" optionValue="value" placeholder="Level" size="small" class="w-32" />
        <Select v-model="filterSemester" :options="termOptionsWithAll" optionLabel="label" optionValue="value" placeholder="Semester" size="small" class="w-36" />
      </template>
    </BillingFilterBar>

    <!-- Curriculum Table Card -->
    <div v-if="curriculumView === 'curriculum'" class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <!-- Card Header -->
      <div class="px-5 py-4 border-b border-gray-300">
        <div class="flex items-center gap-3">
          <h3 class="font-semibold text-gray-700">
            All Curriculum
            <span v-if="totalRecords" class="text-sm font-normal text-gray-400 ml-1">({{ totalRecords }})</span>
          </h3>

          <!-- Layout toggle: switch the table to a grouped view -->
          <div class="flex rounded-lg border border-gray-300 overflow-hidden shrink-0">
              <button
                type="button"
                v-tooltip.top="'Table view'"
                class="px-2.5 py-1.5 text-sm transition-colors"
                :class="curriculumLayout === 'table' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-50'"
                @click="curriculumLayout = 'table'"
              >
                <i class="pi pi-table"></i>
              </button>
              <button
                type="button"
                v-tooltip.top="'Grouped view'"
                class="px-2.5 py-1.5 text-sm transition-colors border-l border-gray-300"
                :class="curriculumLayout === 'grouped' ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-50'"
                @click="curriculumLayout = 'grouped'"
              >
                <i class="pi pi-sitemap"></i>
              </button>
            </div>
        </div>
      </div>

      <!-- Table Body -->
      <div class="p-0">
        <template v-if="loadingSchoolCurriculum">
          <div class="flex items-center justify-center py-16">
            <span class="pi pi-spin pi-spinner text-3xl text-primary"></span>
          </div>
        </template>
        <template v-else>
          <!-- Grouped layout (inner switch of the table) -->
          <div v-if="curriculumLayout === 'grouped'" class="p-4">
            <CurriculumAccordionView
              :rows="curriculumTableData"
              @edit="handleEdit"
              @delete="handleDelete"
            />
          </div>

          <!-- Table layout -->
          <template v-else>
          <!-- Bulk Action Bar -->
          <div v-if="selectedCurriculumRows.length > 0" class="flex flex-wrap items-center gap-2 px-3 py-2 mb-2 bg-primary/10 border border-primary/20 rounded-lg">
            <span class="text-xs font-semibold text-primary-700">{{ selectedCurriculumRows.length }} selected</span>
            <div class="flex-1"></div>
            <Button label="Set Compulsory" icon="pi pi-check" size="small" outlined
              :loading="bulkUpdating" @click="bulkSetField('status', 'C')" />
            <Button label="Set Elective" icon="pi pi-minus" size="small" severity="secondary" outlined
              :loading="bulkUpdating" @click="bulkSetField('status', 'E')" />
            <Select v-model="bulkLevel" :options="levelOptionsList" optionLabel="label" optionValue="value"
              placeholder="Change Level" size="small" class="w-36" @change="bulkSetField('course_registration_level', bulkLevel)" />
            <Select v-model="bulkSemester" :options="semesterOptions" optionLabel="label" optionValue="value"
              placeholder="Change Semester" size="small" class="w-40" @change="bulkSetField('semester', bulkSemester)" />
            <Select v-model="bulkModuleId" :options="bulkModuleOptions" optionLabel="label" optionValue="id"
              :disabled="!bulkModuleProgramme" placeholder="Add to module" size="small" class="w-64" filter />
            <Button label="Assign module" size="small" :disabled="!bulkModuleId" :loading="bulkUpdating" @click="bulkSetField('programme_module_id', bulkModuleId)" />
            <Button label="Remove from module" size="small" severity="secondary" outlined :disabled="!bulkModuleId" :loading="bulkUpdating"
              @click="bulkSetField('programme_module_id', bulkModuleId, true)" />
            <Select v-model="bulkProgrammeId" :options="programmeOptions" optionLabel="label" optionValue="id"
              placeholder="Change Programme" size="small" class="w-52" filter @change="bulkSetField('programme_id', bulkProgrammeId)" />
            <Button icon="pi pi-times" text rounded size="small" severity="secondary"
              @click="selectedCurriculumRows = []" v-tooltip.top="'Clear'" />
            <p class="text-xs text-gray-500 w-full">
              Assign adds an offering. Remove clears only the selected module's offering; other sessions and existing student registrations are preserved.
            </p>
          </div>

          <div class="overflow-x-auto">
            <ReusablePrimeTable :headers="curriculumHeaders" :data="curriculumTableData"
              emptyMessage="No curriculum found" tableStyle="min-width: 600px;"
              :showCheckbox="true"
              v-model:selection="selectedCurriculumRows"
              :paginator="true"
              :lazy="true"
              :rows="rowsPerPage"
              :totalRecords="totalRecords"
              :first="firstRecord"
              @page="onPageChange"
            >
              <template #status="{ row }">
                <Tag
                  :value="row.status"
                  :severity="row.status === 'Compulsory' ? undefined : 'secondary'"
                  rounded
                />
              </template>
              <template #actions="{ row }">
                <div class="flex gap-1 items-center">
                  <Button icon="pi pi-pencil" class="p-button-text p-button-rounded p-button-sm"
                    @click="handleEdit(row)" v-tooltip.top="'Edit'" />
                  <Button icon="pi pi-trash" class="p-button-danger p-button-text p-button-rounded p-button-sm"
                    @click="handleDelete(row)" v-tooltip.top="'Delete'" />
                </div>
              </template>
            </ReusablePrimeTable>
          </div>
          </template>
        </template>
      </div>
    </div>

    <!-- Delete Dialog -->
    <Dialog v-model:visible="showDeleteDialog" header="Delete Curriculum Entry" modal :style="{ width: '420px' }">
      <div class="flex items-start gap-3 pt-2">
        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 shrink-0">
          <i class="pi pi-trash text-red-500"></i>
        </div>
        <div>
          <p class="font-medium text-gray-800">Are you sure?</p>
          <p class="text-sm text-gray-500 mt-1">
            This will permanently delete the curriculum entry
            <strong v-if="curriculumToDelete">
              ({{ curriculumToDelete.programme_name }} — Level {{ curriculumToDelete.course_registration_level }},
              {{ curriculumToDelete.semester }})
            </strong>. This action cannot be undone.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-6">
        <Button size="small" label="Cancel" severity="secondary" @click="showDeleteDialog = false" />
        <Button size="small" label="Delete" severity="danger" icon="pi pi-trash" @click="confirmDelete" />
      </div>
    </Dialog>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEditDialog" header="Edit Curriculum Entry" modal :style="{ width: '600px' }">
      <div class="space-y-4 pt-2">
        <div class="flex items-start gap-2 bg-primary/10 border border-primary/20 px-3 py-2 rounded-lg">
          <i class="pi pi-info-circle text-primary mt-0.5 shrink-0 text-xs"></i>
          <span class="text-xs text-primary-700">
            <strong>Level:</strong> Use <em>Any Level (*)</em> for all levels.
            <strong>Semester:</strong> Use <em>Any Semester (0)</em> for both semesters.
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Course</label>
          <Select v-model="editCourseId" :options="courseOptions" optionLabel="label" optionValue="value"
            placeholder="Select Course" class="w-full" filter
            :virtualScrollerOptions="{ itemSize: 38 }" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Programme</label>
          <Select v-model="editProgrammeId" :options="programmeOptions" optionLabel="label" optionValue="id"
            placeholder="Select Programme" class="w-full" filter />
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Level</label>
            <Select v-model="editCourseRegistrationLevel" :options="levelOptions" optionLabel="label" optionValue="value"
              placeholder="Level" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Semester</label>
            <Select v-model="editSemester" :options="editTermOptions" optionLabel="label" optionValue="value"
              placeholder="Semester" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Status</label>
            <Select v-model="editStatus" :options="statusOptions" optionLabel="label" optionValue="value"
              placeholder="Status" class="w-full" />
          </div>
        </div>
        <div v-if="editUsesModules" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Primary module</label>
          <Select v-model="editProgrammeModuleId" :options="editModuleOptions" optionLabel="label"
            optionValue="value" placeholder="No module" class="w-full" showClear
            :loading="loadingEditModules" data-testid="curriculum-edit-module-select" />
          <p class="text-xs text-gray-500">
            Changing or clearing this field removes the previous primary module offering.
            Use “Remove from module” on selected rows to remove other offerings.
          </p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-6">
        <Button size="small" label="Cancel" severity="secondary" @click="showEditDialog = false" />
        <Button size="small" :loading="isSubmitting" label="Save Changes" @click="confirmUpdate" />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useModuleCurriculumMutations } from '@/composables/mutations/tertiary/useModuleCurriculumMutations';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useProgrammeModules } from '@/composables/queries/useProgrammeModules';
import { ProgrammeDeliveryModeEnum, ProgressionTypeEnum } from '@/types/composition-functions';
import { QUERY_ALL_SESSION_STRUCTURES } from '@/graphql';
import { useCourses } from '@/composables/queries/useCourses';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useCurriculum } from '@/composables/queries/useCurriculum';
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import UnfloatedCourses from './UnfloatedCourses.vue';
import CoursePrerequisites from './CoursePrerequisites.vue';
import CurriculumAccordionView from './CurriculumAccordionView.vue';
import SlideOverDrawer from '@/components/common/SlideOverDrawer.vue';
import { useToast } from 'primevue/usetoast';
import { getErrorMessage } from '@/utils/error';
import { formatCourseInstance } from '@/utils/courseInstance';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import type {
  CurriculumCourse as Course,
  CurriculumProgramme as Programme,
  CurriculumItem,
  CurriculumTableRow,
  CurriculumEditModel,
} from './curriculumRows';
import { buildCurriculumRow, rowToEditModel } from './curriculumRows';
import { curriculumFormRules } from './curriculumFormRules';
import { useValidatedForm } from '@/composables/useValidatedForm';

const toast = useToast();
const userStore = useUserStore();

// Mirrors the Prerequisites tab gate in useTertiaryDashboard: the prerequisite
// screens sit behind the course-equivalence permission family.
const canViewPrerequisites = computed(() =>
  can(userStore.currentUser, AppPermissions.CourseEquivalence.Actions.View, AppPermissions.CourseEquivalence.Resource),
);

const curriculumView = ref<'curriculum' | 'unfloated' | 'prerequisites'>('curriculum');
const curriculumViewOptions = computed(() => [
  { label: 'Curriculum', value: 'curriculum' },
  { label: 'Unfloated Courses', value: 'unfloated' },
  ...(canViewPrerequisites.value ? [{ label: 'Prerequisites', value: 'prerequisites' }] : []),
]);

// Inner layout of the curriculum tab: classic table vs grouped (accordion)
const curriculumLayout = ref<'table' | 'grouped'>('table');

const showCreateForm = ref(false);

const { createTertiaryCurriculum, updateTertiaryCurriculum, deleteTertiaryCurriculum } = useTertiaryMutations();

const programme_id = ref('');
const course_registration_level = ref<string | ''>('');
const semester = ref<number | ''>('');
const programme_module_id = ref<string | null>(null);
const coursesSelected = ref<{ course_id: string; status: string }[]>([
  { course_id: '', status: '' },
]);

const statusOptions = [
  { label: 'Compulsory', value: 'C' },
  { label: 'Elective', value: 'E' },
];

type deleteTertiaryCurriculum = {
  id: string;
  programme_name: string;
  course_registration_level: string;
  semester: string;
  status: string;
  course_titles: string;
  course_units: string;
};

const showDeleteDialog = ref(false);
const curriculumToDelete = ref<deleteTertiaryCurriculum>();

// Bulk update
const selectedCurriculumRows = ref<Record<string, unknown>[]>([]);
const bulkUpdating = ref(false);
const bulkLevel = ref<number | null>(null);
const bulkSemester = ref<number | null>(null);
const bulkProgrammeId = ref<string | null>(null);

const levelOptionsList = computed(() => levelOptions.value);
const semesterOptions = [{ label: 'Any Semester (0)', value: 0 }, { label: '1st Semester', value: 1 }, { label: '2nd Semester', value: 2 }];

const { bulkUpdateMutate } = useModuleCurriculumMutations();
const bulkModuleId = ref<string | null>(null);
const bulkModuleProgramme = computed(() => {
  const ids = [...new Set(selectedCurriculumRows.value.map(r => String(r.programme_id ?? '')))];
  return ids.length === 1 ? (ids[0] ?? '') : '';
});
const { modules: bulkModules } = useProgrammeModules(computed(() => ({ programme_id: bulkModuleProgramme.value })));
const bulkModuleOptions = computed(() => bulkModules.value.map(m => ({ id: m.id, label: `${m.name} · ${m.started_at} – ${m.ended_at}` })));
watch(bulkModuleProgramme, () => { bulkModuleId.value = null; });

async function bulkSetField(field: string, value: string | number | null, removeFromModule = false) {
  // `value === 0` is the legitimate "Any Semester (0)" choice — no truthiness here.
  if (value === null || value === undefined || value === '' || !selectedCurriculumRows.value.length) return;
  bulkUpdating.value = true;
  try {
    const ids = selectedCurriculumRows.value.map((r) => String(r.id));
    const vars: Record<string, unknown> = { ids, remove_from_module: removeFromModule };
    // course_registration_level is a String in the schema; semester/others keep their types.
    vars[field] = field === 'course_registration_level' ? String(value) : value;
    const result = await bulkUpdateMutate(vars);
    const msg = result?.data?.bulkUpdateCurriculum;
    if (!msg?.success) {
      toast.add({ severity: 'error', summary: 'Update Failed', detail: msg?.message || 'Failed to update curriculum.', life: 5000 });
      return;
    }
    toast.add({ severity: 'success', summary: 'Updated', detail: msg.message, life: 3000 });
    selectedCurriculumRows.value = [];
    bulkLevel.value = null;
    bulkSemester.value = null;
    bulkProgrammeId.value = null;
    refetchSchoolCurriculum();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  } finally {
    bulkUpdating.value = false;
  }
}

const showEditDialog = ref(false);
const curriculumToEdit = ref<CurriculumEditModel | null>(null);

const editProgrammeId = ref('');
const editCourseRegistrationLevel = ref<string>('');
const editSemester = ref<number | ''>('');
const editStatus = ref('');
const editCourseId = ref('');
const editProgrammeModuleId = ref<string | null>(null);

const { programmes, paginatorInfo, setPage } = useProgrammes({
  autoFetch: true,
  first: 200,
  page: 1,
});

const handleProgrammeScroll = async(event: Event) => {
  const target = event.target as HTMLElement;
  const { scrollTop, scrollHeight, clientHeight } = target;
  const bottomReached = scrollTop + clientHeight >= scrollHeight - 5;
  if (bottomReached && paginatorInfo.value && paginatorInfo.value.currentPage < paginatorInfo.value.lastPage) {
    await setPage(paginatorInfo.value.currentPage + 1);
  }
};

const { courses, refetchSchoolCourses } = useCourses({ autoFetch: true, first: 1000 });
const { classrooms } = useSchoolQuery();
const { curriculum, loadingSchoolCurriculum, refetchSchoolCurriculum, paginatorInfo: curriculumPaginatorInfo, setItemsPerPage, setPage: curriculumSetPage, programmeId,
  level, semester: curriculumSemester, search } = useCurriculum({ autoFetch: true });

const filterProgrammeId = ref<string | ''>('');
const filterLevel = ref<string>('');
const filterSemester = ref<number | ''>('');

const filterChips = computed(() => {
  const chips: { key: string; label: string }[] = [];
  if (filterProgrammeId.value) {
    const p = programmeOptions.value.find((o: { id: string; label: string }) => o.id === filterProgrammeId.value);
    chips.push({ key: 'programme', label: `Programme: ${p?.label ?? filterProgrammeId.value}` });
  }
  if (filterLevel.value) {
    chips.push({ key: 'level', label: `Level: ${filterLevel.value}` });
  }
  if (filterSemester.value !== '') {
    const s = termOptions.value.find((o: { value: number | string; label: string }) => o.value === filterSemester.value);
    chips.push({ key: 'semester', label: `Semester: ${s?.label ?? filterSemester.value}` });
  }
  return chips;
});

function removeFilterChip(key: string) {
  if (key === 'programme') filterProgrammeId.value = '';
  else if (key === 'level') filterLevel.value = '';
  else if (key === 'semester') filterSemester.value = '';
}

function clearAllFilters() {
  filterProgrammeId.value = '';
  filterLevel.value = '';
  filterSemester.value = '';
  search.value = '';
}

const programmeOptionsWithAll = computed(() => [
  { label: 'All Programmes', value: '' },
  ...programmeOptions.value.map((p: { label: string; id: string }) => ({
    label: p.label,
    value: p.id,
  })),
]);

const levelOptionsWithAll = computed(() => [
  { label: 'All Levels', value: '' },
  ...levelOptions.value,
]);

const termOptionsWithAll = computed(() => [
  { label: 'All Semesters', value: '' },
  ...termOptions.value,
]);

const rowsPerPage = computed(() => Number(curriculumPaginatorInfo?.value?.perPage) || 50);
const totalRecords = computed(() => Number(curriculumPaginatorInfo?.value?.total) || 0);
const firstRecord = computed(() => (((Number(curriculumPaginatorInfo?.value?.currentPage) || 1) - 1) * rowsPerPage.value) || 0);

async function onPageChange(event: { page?: number; first?: number; rows?: number }) {
  try {
    if (typeof event.page === 'number') {
      await setPage(event.page + 1);
    } else if (typeof event.first === 'number' && typeof event.rows === 'number') {
      const newPage = Math.floor(event.first / event.rows) + 1;
      if (event.rows !== rowsPerPage.value) {
        await setItemsPerPage(event.rows);
      } else {
        await curriculumSetPage(newPage);
      }
    }
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unable to change page', life: 3000 });
  }
}

const { result: sessionStructures } = QUERY_ALL_SESSION_STRUCTURES();

const termOptions = computed(() => {
  const structures = sessionStructures.value?.allSessionStructures || [];
  const allTerms = structures.flatMap((s: { termsStructure?: Array<{ name: string; sequence?: number }> }) => s.termsStructure || []);
  const uniqueTerms = new Map<string, { label: string; value: number | string }>();

  allTerms.forEach((t: { name: string; sequence?: number }) => {
    if (uniqueTerms.has(t.name)) return;
    let value: number | string = t.name;
    if (typeof t.sequence === 'number' && Number.isFinite(t.sequence)) {
      value = t.sequence;
    } else {
      const name = (t.name || '').trim();
      const match = name.match(/^\s*(\d+)(?:st|nd|rd|th)?/i) || name.match(/(\d+)(?:st|nd|rd|th)/i);
      if (match) value = Number(match[1]);
      else {
        const lower = name.toLowerCase();
        if (lower.includes('first')) value = 1;
        else if (lower.includes('second')) value = 2;
      }
    }
    uniqueTerms.set(t.name, { label: t.name, value });
  });

  return [
    { label: 'Any Semester (0)', value: 0 },
    ...Array.from(uniqueTerms.values()),
  ];
});

const levelOptions = computed(() => [
  { label: 'Any Level (*)', value: '*' },
  ...(classrooms.value?.filter(c => c?.name?.trim()).map(c => ({
    label: c?.name || '',
    value: c?.name || '',
  })) || []),
]);

const programmeOptions = computed(() =>
  programmes.value?.map((p: Programme) => ({
    label: `${p.program_name}${p.degree ? ` — ${p.degree}` : ''}${p.type ? ` — ${p.type}` : ''}`,
    id: p.id,
  })) || [],
);

const courseOptions = computed(() =>
  courses.value?.map((c: Course) => ({
    label: `${c.course_title} (${formatCourseInstance(c)}) - ${c.unit} Unit${c.unit !== 1 ? 's' : ''}`,
    value: c.id,
  })) || [],
);

// ─── Drawer footer summary ───────────────────────────────────────
const courseUnitMap = computed(() => {
  const map = new Map<string, number>();
  (courses.value || []).forEach((c: Course) => map.set(c.id, Number(c.unit) || 0));
  return map;
});

const selectedCourseCount = computed(() =>
  coursesSelected.value.filter(c => c.course_id).length,
);

const selectedUnitTotal = computed(() =>
  coursesSelected.value.reduce(
    (sum, c) => sum + (c.course_id ? courseUnitMap.value.get(c.course_id) || 0 : 0),
    0,
  ),
);

// ─── Programme modules (modular/residence programmes) ────────────
function programmeUsesModules(id: string): boolean {
  const programme = programmes.value?.find((p: Programme) => p.id === id);
  return programme?.delivery_mode === ProgrammeDeliveryModeEnum.Modular
    || programme?.delivery_mode === ProgrammeDeliveryModeEnum.Residence;
}

function getSelectedProgramme(id: string): Programme | undefined {
  return programmes.value?.find((p: Programme) => p.id === id);
}

const selectedProgrammeIsYearBased = computed(() => {
  if (!programme_id.value) return false;
  const prog = getSelectedProgramme(programme_id.value);
  return prog?.progression_type === ProgressionTypeEnum.Year;
});

const editProgrammeIsYearBased = computed(() => {
  if (!editProgrammeId.value) return false;
  const prog = getSelectedProgramme(editProgrammeId.value);
  return prog?.progression_type === ProgressionTypeEnum.Year;
});

function buildCumulativeSemesterOptions(programmeId: string): { label: string; value: number }[] {
  const prog = getSelectedProgramme(programmeId);
  const duration = prog?.duration ?? 8;
  const options: { label: string; value: number }[] = [{ label: 'Any Semester (0)', value: 0 }];
  for (let s = 1; s <= duration; s++) {
    const year = Math.ceil(s / 2);
    const semInYear = ((s - 1) % 2) + 1;
    options.push({ label: `Y${year} Sem ${semInYear} (${s})`, value: s });
  }
  return options;
}

const createUsesModules = computed(() => !!programme_id.value && programmeUsesModules(programme_id.value));
const editUsesModules = computed(() => !!editProgrammeId.value && programmeUsesModules(editProgrammeId.value));

const createTermOptions = computed(() => {
  if (selectedProgrammeIsYearBased.value) return buildCumulativeSemesterOptions(programme_id.value);
  return termOptions.value;
});

const editTermOptions = computed(() => {
  if (editProgrammeIsYearBased.value) return buildCumulativeSemesterOptions(editProgrammeId.value);
  return termOptions.value;
});

const createModuleVariables = computed(() => ({ programme_id: programme_id.value }));
const { modules: createModules, loadingModules: loadingCreateModules } = useProgrammeModules(createModuleVariables);
const editModuleVariables = computed(() => ({ programme_id: editProgrammeId.value }));
const { modules: editModules, loadingModules: loadingEditModules } = useProgrammeModules(editModuleVariables);

const moduleStatusWord = (m: { is_open?: boolean; is_active?: boolean }) =>
  m.is_open ? 'open' : (m.is_active ? 'out of window' : 'inactive');
const toModuleOptions = (list: { id: string; name: string; started_at: string; ended_at: string; is_open?: boolean; is_active?: boolean }[]) =>
  list.map(m => ({ label: `${m.name} (${m.started_at} – ${m.ended_at}) — ${moduleStatusWord(m)}`, value: m.id }));
const createModuleOptions = computed(() => toModuleOptions(createModules.value));
const editModuleOptions = computed(() => toModuleOptions(editModules.value));

watch(programme_id, () => {
  programme_module_id.value = null;
  semester.value = '';
});

function addCourse() {
  coursesSelected.value.push({ course_id: '', status: '' });
}
function removeCourse(index: number) {
  coursesSelected.value.splice(index, 1);
}

const payload = computed(() => {
  const sem = Number(semester.value ?? 0);
  return {
    programme_id: programme_id.value,
    course_registration_level: course_registration_level.value || '',
    semester: Number.isNaN(sem) ? 0 : sem,
    programme_module_id: (createUsesModules.value && programme_module_id.value) || undefined,
    course: coursesSelected.value
      .map((c: { course_id: string; status: string }) => ({ course_id: c.course_id, status: c.status }))
      .filter((c: { course_id: string; status: string }) => !!c.course_id && !!c.status),
  };
});

const isSubmitting = ref(false);

const createForm = reactive(useValidatedForm(curriculumFormRules, reactive({
  programme_id,
  course_registration_level,
  semester,
  course: computed(() => payload.value.course),
})));
watch(showCreateForm, (open) => {
  if (open) createForm.reset();
});

async function submit() {
  const semNum = Number(semester.value);
  const levelVal = course_registration_level.value?.trim();

  if (!(await createForm.validate())) return;

  isSubmitting.value = true;
  try {
    const input = {
      ...payload.value,
      semester: semNum,
      course_registration_level: levelVal ?? '',
    };
    // createTertiaryCurriculum toasts the success itself.
    await createTertiaryCurriculum(input);
    await refetchSchoolCurriculum();
    programme_id.value = '';
    course_registration_level.value = '';
    semester.value = '';
    programme_module_id.value = null;
    coursesSelected.value = [{ course_id: '', status: '' }];
    createForm.reset();
  } catch (err: unknown) {
    // createTertiaryCurriculum has already toasted the reason; put field
    // messages under their fields.
    createForm.handleServerErrors(err);
  } finally {
    isSubmitting.value = false;
  }
}

const curriculumHeaders = [
  { field: 'programme_name', header: 'Programme' },
  { field: 'course_registration_level', header: 'Level' },
  { field: 'semester', header: 'Semester' },
  { field: 'module_name', header: 'Module' },
  { field: 'status', header: 'Status' },
  { field: 'course_titles', header: 'Courses' },
  { field: 'course_units', header: 'Courses Unit' },
  { field: 'course_codes', header: 'Course Codes' },
  { field: 'course_instances', header: 'Course Instances' },
  { field: 'actions', header: 'Actions' },
];

const curriculumTableData = computed(() => {
  if (!curriculum.value) return [] as CurriculumTableRow[];
  return (curriculum.value as CurriculumItem[]).map(buildCurriculumRow);
});

async function confirmUpdate() {
  if (!curriculumToEdit.value) return;

  const semNum = Number(editSemester.value);
  const levelVal = String(editCourseRegistrationLevel.value ?? '').trim();

  // The explicit '' check matters: Number('') is 0, which would otherwise slip
  // through as a silent "any semester" rewrite.
  if (!editProgrammeId.value || !levelVal || editSemester.value === '' || !Number.isFinite(semNum) || semNum < 0) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill all required fields', life: 3000 });
    return;
  }

  isSubmitting.value = true;
  try {
    const input = {
      programme_id: editProgrammeId.value,
      course_registration_level: levelVal,
      semester: semNum,
      status: editStatus.value || '',
      course_id: editCourseId.value || '',
      // Always sent: the backend carries the old assignment over when the key
      // is absent, so moving a module-assigned entry to a regular programme
      // must explicitly clear it (null) or the move is rejected outright.
      programme_module_id: editUsesModules.value ? editProgrammeModuleId.value : null,
    };
    await updateTertiaryCurriculum(curriculumToEdit.value.id, input);
    await refetchSchoolCurriculum();
    toast.add({ severity: 'success', summary: 'Updated', detail: 'Curriculum updated successfully', life: 3000 });
    showEditDialog.value = false;
    curriculumToEdit.value = null;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error updating curriculum';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    isSubmitting.value = false;
  }
}

async function confirmDelete() {
  if (!curriculumToDelete.value) return;
  try {
    await deleteTertiaryCurriculum(curriculumToDelete.value.id);
    await refetchSchoolCurriculum();
    toast.add({ severity: 'success', summary: 'Deleted', detail: 'Curriculum entry deleted', life: 3000 });
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'An unexpected error occurred';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    showDeleteDialog.value = false;
    curriculumToDelete.value = undefined;
  }
}

function handleEdit(row: CurriculumTableRow) {
  // The edit model reads the raw numeric semester carried on the row
  // (semester_value) — never the rendered label.
  curriculumToEdit.value = rowToEditModel(row);

  editProgrammeId.value = curriculumToEdit.value.programme_id;
  editCourseRegistrationLevel.value = curriculumToEdit.value.course_registration_level;
  editSemester.value = curriculumToEdit.value.semester;
  editStatus.value = curriculumToEdit.value.status;
  editCourseId.value = curriculumToEdit.value.course_id;
  editProgrammeModuleId.value = row.programme_module_id ?? null;
  showEditDialog.value = true;
}

// Moving an entry to another programme invalidates its module assignment
watch(editProgrammeId, (newId) => {
  if (newId !== curriculumToEdit.value?.programme_id) {
    editProgrammeModuleId.value = null;
  }
});

function handleDelete(row: CurriculumTableRow) {
  curriculumToDelete.value = row;
  showDeleteDialog.value = true;
}

watch(filterProgrammeId, v => programmeId.value = v);
watch(filterLevel, v => level.value = v);
watch(filterSemester, v => curriculumSemester.value = v);
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.25s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
